import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { BaseApiService, ApiResponse } from './base-api.service';
import { environment } from '@environments/environment';

import { Project, ProjectCreate, ProjectPdfExportPayload } from '@core/models/projects.model';

@Injectable({
    providedIn: 'root',
})
export class ProjectsApiService extends BaseApiService {
    exportProjectReportPdf(body: ProjectPdfExportPayload): Observable<Blob> {
        return this.post<Blob>(
            '/project/reporting-pdf',
            { ...body, template: 'project' },
            {
                baseUrl: environment.ip_reporting,
                responseType: 'blob',
                timeout: 60000,
            },
        );
    }

    getProjects(): Observable<Project[]> {
        return this.get<ApiResponse<Project[]>>('/projects', undefined, {
            baseUrl: environment.ip_reporting,
        }).pipe(
            map((response) => {
                if (response.code == 200 && response.data) {
                    return response.data;
                }
                return [];
            }),
        );
    }

    getProjectCampaignsBatch(campaignIds: string[], offset: number, limit: number = 3): Observable<any> {
        const campaignIdsParam = JSON.stringify(campaignIds);
        const url = `/project/batch?offset=${offset}&limit=${limit}&campaign_ids=${campaignIdsParam}`;
        return this.get<any>(url, undefined, {
            baseUrl: environment.ip_reporting,
        }).pipe(
            map((response) => {
                if (response) {
                    const transformedResponse = {
                        ...response.data,
                        datas: this.transformCampaignDates(response.data.datas || []),
                    };
                    return transformedResponse;
                }
                return {
                    datas: [],
                    datas_only_metrics_by_type: [],
                };
            }),
        );
    }

    private transformCampaignDates(campaigns: any[]): any[] {
        return campaigns.map((campaign) => ({
            ...campaign,
            begin_date_formatted: this.formatDate(campaign.begin_date),
            end_date_formatted: this.formatDate(campaign.end_date),
            data_by_day:
                campaign.data_by_day?.map((day: any) => ({
                    ...day,
                    date_formatted: this.formatDate(day.date),
                })) || [],
        }));
    }

    private formatDate(dateString: string | null | undefined): string {
        if (!dateString) return '';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();

            return `${day}/${month}/${year}`;
        } catch {
            return dateString;
        }
    }

    createProject(body: ProjectCreate): Observable<any> {
        return this.post<any>('/project', body, {
            baseUrl: environment.ip_reporting,
        }).pipe(
            map((response) => {
                if (response.code == 200 && response.project_id) {
                    return response.project_id;
                }
                throw new Error(response.message || 'Failed to create project');
            }),
        );
    }

    deleteProject(projectId: number): Observable<any> {
        return this.delete<any>(`/project/${projectId}`, {
            baseUrl: environment.ip_reporting,
        }).pipe(
            map((response) => {
                if (response.code == 200) {
                    return true;
                }
                throw new Error(response.message || 'Failed to delete project');
            }),
        );
    }

    updateProject(projectId: number, body: ProjectCreate): Observable<{ success: boolean; message: string }> {
        return this.put<any>(`/project/${projectId}`, body, {
            baseUrl: environment.ip_reporting,
        }).pipe(
            map((response) => {
                if (response.code == 200) {
                    return { success: true, message: response.message };
                }
                throw new Error(response.message || 'Failed to update project');
            }),
        );
    }
}
